import React, {useCallback, useEffect, useMemo} from 'react';
import {ReactComponent as AwardIcon} from 'icons/check-award.svg';
import {useAppTranslation} from 'services/i18n';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, Tooltip} from '@mui/material';
import {useSelector} from 'react-redux';
import {selectAuthUser, selectContentViewer} from 'store/selectors';
import {confirmLesson} from 'store/contentViewer';
import {useAppDispatch} from 'store';
import {ContentLayoutWithControls} from './ContentLayoutWithControls';
import {ErrorOutlined} from "@mui/icons-material";
import {Link} from "components/clickable/Link";

export enum ButtonsLoading {
  SignedCertificate = 'signedCertificate',
  UnsignedCertificate = 'unsignedCertificate',
}

export const Certificate = () => {
  const t = useAppTranslation();
  const {courseId} = useParams();
  const navigate = useNavigate();

  const authUser = useSelector(selectAuthUser);

  const contentViewerState = useSelector(selectContentViewer);

  const confirmedAt = useMemo(() => {
    return contentViewerState?.course?.confirmed_at
      ? new Date(contentViewerState?.course.confirmed_at)
      : undefined;
  }, [contentViewerState?.course?.confirmed_at]);

  const isCourseCompleted =
    contentViewerState?.course?.state && contentViewerState.course.state === 'completed';

  const dispatch = useAppDispatch();

  const confirmCourseCompletion = useCallback(async () => {
    if (courseId && !confirmedAt) {
      await dispatch(
        confirmLesson({
          id: courseId,
          body: {},
        })
      );
    }
  }, [courseId, confirmedAt, dispatch]);

  const handleShowCertificate = useCallback(async (buttonLoading?: ButtonsLoading) => {
    if (buttonLoading === ButtonsLoading.SignedCertificate && !confirmedAt) {
      await confirmCourseCompletion();
    }
    courseId &&
    window.open(`api/v1/content_viewer/participations/${courseId}/certificate.html`, '_blank');
  }, [confirmedAt, courseId, confirmCourseCompletion]);

  const keyboardProps = useMemo(() => ({
    leftKeyText: t('contentViewer.keyboardControls.backToCourse'),
    onLeftKeyPress: () => navigate(`/learn/${courseId}`),
    enterKeyText: t('contentViewer.keyboardControls.certificate'),
    isEnterKeyAction: !!contentViewerState?.course?.certificate_available,
    onEnterKeyPress: () => {
      return handleShowCertificate(ButtonsLoading.SignedCertificate);
    },
  }), [courseId, contentViewerState?.course?.certificate_available, handleShowCertificate, navigate, t]);

  useEffect(() => {
    isCourseCompleted && confirmedAt && navigate(`/learn/${courseId}`);
  }, [isCourseCompleted, confirmedAt, courseId, navigate]);

  const content = useMemo(() => {
    if (!contentViewerState?.course) {
      return null;
    }

    if (!contentViewerState.course.certificate_available) {
      return <>
        <Box
          className={
            'tw-flex tw-h-48 tw-w-48 tw-items-center tw-justify-center tw-rounded-xl '
          }
          sx={{
            background: 'var(--color-warning)',
            color: 'var(--color-white)'
          }}
        >
          <ErrorOutlined/>
        </Box>

        <h1 className="tw-flex tw-flex-col tw-items-center tw-text-center tw-text-24 tw-font-semibold tw-max-w-500">
          <span>{t('contentViewer.certificate.title')} {t('contentViewer.certificate.missingConfirm')}</span>
        </h1>
        <Box className="tw-mt-8 tw-text-center tw-text-14" sx={{color: 'var(--color-black-text)'}}>
          <p>{t('contentViewer.certificate.missingConfirmInfo')}</p>
        </Box>
        <Box className="tw-flex tw-flex-wrap tw-justify-center tw-gap-16" sx={{
          a: {
            color: 'var(--color-white) !important',
            '&:hover, &:focus': {
              '--tw-border-opacity': 'unset !important',
              opacity: .8
            }
          }
        }}>
          <Link
            kind="outlined"
            className="tw-relative tw-m-0 tw-flex tw-h-48 tw-items-center tw-gap-8 tw-rounded-lg tw-border tw-bg-black tw-py-12 tw-px-14 tw-font-medium"
            href={'#/dashboard'}
          >
            {t('contentViewer.certificate.confirmMissing')}
          </Link>
        </Box>
      </>
    }

    return <>
      <div
        className={
          'tw-flex tw-h-48 tw-w-48 tw-items-center tw-justify-center tw-rounded-xl tw-bg-lightGreen'
        }
      >
        <AwardIcon/>
      </div>

      <h1 className="tw-flex tw-flex-col tw-items-center tw-text-center tw-text-24 tw-font-semibold">
        <span>{t('contentViewer.certificate.title')} {t('contentViewer.certificate.subtitle')}</span>
        <span>{t('contentViewer.certificate.missingStep')}</span>
      </h1>
      <Box className="tw-mt-8 tw-mb-8 tw-max-w-500 tw-text-center tw-bg-white tw-rounded-2xl tw-p-24 tw-pl-48 tw-pr-48">
        <p className={'tw-mb-16 tw-text-14'}>{t('contentViewer.certificate.confirmCompletion')}</p>

        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-16">
          <Tooltip title={<div dangerouslySetInnerHTML={{__html: t('contentViewer.certificate.tooltip')}}/>} enterDelay={500}>
            <button
              className="tw-relative tw-m-0 tw-flex tw-h-48 tw-items-center tw-gap-8 tw-rounded-lg tw-bg-green tw-py-12 tw-px-14 tw-font-medium  hover:tw-opacity-80"
              onClick={() => handleShowCertificate(ButtonsLoading.SignedCertificate)}
            >
              {t('contentViewer.certificate.confirmAndShow')}
              <div className="tw-hidden tw-cursor-pointer tw-rounded tw-bg-black/5 tw-px-5 tw-py-2 tw-text-12  lg:tw-block">
                Enter
              </div>
            </button>
          </Tooltip>
        </div>
      </Box>
      <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-16">
        <Tooltip title={<div dangerouslySetInnerHTML={{__html: t('contentViewer.certificate.tooltip')}}/>} enterDelay={500}>
          <button
            className="tw-relative tw-m-0 tw-flex tw-h-48 tw-items-center tw-gap-8 tw-rounded-lg tw-border tw-bg-black tw-text-white tw-py-12 tw-px-14 tw-font-medium hover:tw-opacity-80"
            onClick={() => handleShowCertificate(ButtonsLoading.UnsignedCertificate)}
          >
            {t('contentViewer.certificate.showNoConfirm')}
          </button>
        </Tooltip>
      </div>
    </>;

  }, [contentViewerState?.course, handleShowCertificate, t]);

  return (
    <ContentLayoutWithControls
      className="tw-mx-auto tw-max-w-600 tw-flex-col tw-items-center tw-place-content-center tw-gap-16 tw-py-16"
      contentLoading={!authUser}
      {...keyboardProps}
    >
      {content}
    </ContentLayoutWithControls>
  );
};
